import { Modal } from '@starsoft/common/components';
import styles from './styles.module.scss';
import { TransactionsLayoutProps } from './props';
import { containerVariants } from '../../Modal/Layout/variants';
import { motion } from 'framer-motion';
import { drawerVariants } from '../../Modal/Layout/variants';
import { useIsMobile } from '@starsoft/common/hooks';
import { SettingLayoutComponentId } from '@starsoft/common/models';
/**
 * TransactionsLayout Component
 * This component is responsible for rendering a modal with transaction-related content.
 * It uses the Modal component from the common components library and applies custom styles.
 *
 * @param {TransactionsLayoutProps} props - The properties for the TransactionsLayout component.
 * @param {React.ReactNode} props.children - The content to be displayed inside the modal.
 * @param {Object} props.modalCallbacks - The callbacks for handling modal state and interactions.
 * @param {boolean} props.modalCallbacks.isOpen - Flag indicating whether the modal is open.
 * @param {Function} props.modalCallbacks.onClose - Callback function to handle closing the modal.
 * @param {Function} props.modalCallbacks.handleContainer - Callback function to handle container click events.
 *
 * @returns {JSX.Element} The rendered component displaying the modal with transaction content.
 *
 * @example
 * // Usage example:
 * <TransactionsLayout
 *   modalCallbacks={{
 *     isOpen: true,
 *     onClose: () => function('Modal closed'),
 *     handleContainer: () => function('Container clicked'),
 *   }}
 * >
 *   <div>Transaction Content</div>
 * </TransactionsLayout>
 */
export default function TransactionsLayout({
  children,
  modalCallbacks: { isOpen, onClose, handleContainer },
  version,
}: TransactionsLayoutProps) {
  const isMobile: boolean = useIsMobile();
  return (
    <Modal open={isOpen} onClose={() => onClose(true)} version={version}>
      <motion.div
        className={`${styles.container} ${version === SettingLayoutComponentId.ModalV2?.toString() ? styles['container--v2'] : ''}`}
        onClick={handleContainer}
        variants={isMobile ? drawerVariants : containerVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
      >
        {children}
      </motion.div>
    </Modal>
  );
}
